import {
  createFirmPhoneV1,
  deleteFirmPhoneV1,
  getFirmPhonesV1,
  searchAvailablePhoneNumbersV1,
  updateFirmPhoneV1,
} from "@/lib/data/requests/firm/firm-phones";
import { queryKeys } from "@/lib/data/static/query-keys";
import { type APIError, type APIResponse } from "@/lib/types/APIResponse";
import { type FirmPhone } from "@/lib/types/Firm";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { toast } from "react-hot-toast";

export const getFirmPhonesQueryOptions = () => ({
  queryKey: [queryKeys.firmPhones],
  queryFn: async () => getFirmPhonesV1(),
});

export const useFirmPhones = () => {
  return useQuery(getFirmPhonesQueryOptions());
};

export const useCreateFirmPhone = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createFirmPhoneV1,
    onMutate: (data) => {
      const optimisticPhone: FirmPhone = {
        ...data,
        dateCreated: null,
        dateModified: null,
        description: null,
        firmId: "",
        friendlyName: null,
        id: "temp",
        label: data.label,
        number: data.number,
        sid: null,
      };
      queryClient.setQueryData(
        [queryKeys.firmPhones],
        (oldData: APIResponse<FirmPhone[]>) => {
          return {
            ...oldData,
            data: [...oldData.data, optimisticPhone],
          };
        },
      );
      return {
        previousData: queryClient.getQueryData([queryKeys.firmPhones]),
      };
    },
    onError: (error: AxiosError<APIError>, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData([queryKeys.firmPhones], context.previousData);
      }
      console.error(error);
      toastAPIErrorMessage(error, "Error creating firm phone");
    },
    onSuccess: () => {
      toast.success("Firm phone created successfully");
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: [queryKeys.firmPhones] });
    },
  });
};

export const useUpdateFirmPhone = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateFirmPhoneV1,
    onMutate: (data) => {
      queryClient.setQueryData(
        [queryKeys.firmPhones],
        (oldData: APIResponse<FirmPhone[]>) => {
          return {
            ...oldData,
            data: oldData.data.map((phone) =>
              phone.id === data.id ? { ...phone, ...data } : phone,
            ),
          };
        },
      );
      return {
        previousData: queryClient.getQueryData([queryKeys.firmPhones]),
      };
    },
    onError: (error: AxiosError<APIError>, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData([queryKeys.firmPhones], context.previousData);
      }
      toastAPIErrorMessage(error, "Error updating firm phone");
    },
    onSuccess: () => {
      toast.success("Firm phone updated successfully");
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: [queryKeys.firmPhones] });
    },
  });
};

export const useDeleteFirmPhone = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteFirmPhoneV1,
    onMutate: (data) => {
      queryClient.setQueryData(
        [queryKeys.firmPhones],
        (oldData: APIResponse<FirmPhone[]>) => {
          return {
            ...oldData,
            data: oldData.data.filter((phone) => phone.id !== data.id),
          };
        },
      );
      return {
        previousData: queryClient.getQueryData([queryKeys.firmPhones]),
      };
    },
    onError: (error: AxiosError<APIError>, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData([queryKeys.firmPhones], context.previousData);
      }
      toastAPIErrorMessage(error, "Error deleting firm phone");
    },
    onSuccess: () => {
      toast.success("Firm phone deleted successfully");
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: [queryKeys.firmPhones] });
    },
  });
};

export const getAvailablePhoneNumbersQueryOptions = (
  data: Parameters<typeof searchAvailablePhoneNumbersV1>[0],
) => ({
  queryKey: [queryKeys.availablePhoneNumbers, data],
  queryFn: () => searchAvailablePhoneNumbersV1(data),
  enabled: !!data,
});
